import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Fade,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  StorefrontRounded,
  CheckCircleOutline,
  ErrorOutline,
  TimerOff,
  CreditCard,
} from '@mui/icons-material';
import { BillingService } from '../../services/billingService.js';

// Status constants
const STATUS = {
  LOADING: 'loading',
  BILL_READY: 'bill_ready',
  PAYING: 'paying',
  PAYMENT_INITIATED: 'payment_initiated',
  CONFIRMING: 'confirming',
  PAID: 'paid',
  EXPIRED: 'expired',
  ERROR: 'error',
};

export const BillPage = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [billData, setBillData] = useState(null);
  const [bearerToken, setBearerToken] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [error, setError] = useState(null);
  const [expiresIn, setExpiresIn] = useState(null);

  // ── Fetch bill on mount ─────────────────────────────────────────
  useEffect(() => {
    const fetchBill = async () => {
      try {
        const data = await BillingService.fetchBill(sessionToken);
        setBillData(data);
        setBearerToken(data.bearer_token);
        setExpiresIn(data.expires_in_seconds || 900);
        setStatus(STATUS.BILL_READY);
      } catch (err) {
        const msg = err.message || '';
        if (msg.toLowerCase().includes('expired') || msg.includes('410')) {
          setStatus(STATUS.EXPIRED);
        } else if (msg.toLowerCase().includes('already paid') || msg.includes('409')) {
          setStatus(STATUS.PAID);
        } else {
          setError(msg);
          setStatus(STATUS.ERROR);
        }
      }
    };

    if (sessionToken) fetchBill();
  }, [sessionToken]);

  // ── Countdown timer ─────────────────────────────────────────────
  useEffect(() => {
    if (expiresIn === null || expiresIn <= 0) return;
    if (status === STATUS.PAID || status === STATUS.EXPIRED) return;

    const timer = setInterval(() => {
      setExpiresIn((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setStatus(STATUS.EXPIRED);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresIn, status]);

  // ── Format countdown ────────────────────────────────────────────
  const formatCountdown = (seconds) => {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── Initiate Payment ────────────────────────────────────────────
  const handleInitiatePayment = useCallback(async () => {
    setStatus(STATUS.PAYING);
    try {
      const result = await BillingService.processPayment(
        sessionToken,
        bearerToken,
        { action: 'initiate' }
      );
      setPaymentInfo(result.payment_info || result);
      setStatus(STATUS.PAYMENT_INITIATED);
    } catch (err) {
      setError(err.message);
      setStatus(STATUS.ERROR);
    }
  }, [sessionToken, bearerToken]);

  // ── Confirm Payment ─────────────────────────────────────────────
  const handleConfirmPayment = useCallback(async () => {
    setStatus(STATUS.CONFIRMING);
    try {
      const result = await BillingService.processPayment(
        sessionToken,
        bearerToken,
        {
          action: 'confirm',
          transaction_id: `TXN_${Date.now()}`,
          payment_method: 'upi',
        }
      );
      setReceiptData(result);
      setStatus(STATUS.PAID);
    } catch (err) {
      setError(err.message);
      setStatus(STATUS.ERROR);
    }
  }, [sessionToken, bearerToken]);

  // ── Navigate to receipt ─────────────────────────────────────────
  const handleViewReceipt = () => {
    if (receiptData?.receipt_id) {
      navigate(`/receipt/${receiptData.receipt_id}`);
    }
  };

  // ════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════

  // Wrapper for all minisite screens
  const PageWrapper = ({ children }) => (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'rgba(69, 39, 160, 0.04)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: 2,
        pt: { xs: 2, sm: 4 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          borderRadius: '20px',
          boxShadow: '0 4px 24px rgba(69, 39, 160, 0.08)',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );

  // ── Loading state ───────────────────────────────────────────────
  if (status === STATUS.LOADING) {
    return (
      <PageWrapper>
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Loading your bill...
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  // ── Expired state ───────────────────────────────────────────────
  if (status === STATUS.EXPIRED) {
    return (
      <PageWrapper>
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <TimerOff sx={{ fontSize: 56, color: '#FF9500', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Session Expired
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This payment link has expired. Please ask the restaurant to generate a new QR code.
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  // ── Error state ─────────────────────────────────────────────────
  if (status === STATUS.ERROR) {
    return (
      <PageWrapper>
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <ErrorOutline sx={{ fontSize: 56, color: '#FF3B30', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {error || 'Unable to load the bill. Please try again.'}
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  // ── Paid state ──────────────────────────────────────────────────
  if (status === STATUS.PAID) {
    return (
      <PageWrapper>
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Fade in timeout={600}>
            <Box>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'rgba(52, 199, 89, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5,
                }}
              >
                <CheckCircleOutline sx={{ fontSize: 48, color: '#34C759' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                Payment Successful!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Thank you for your payment
              </Typography>

              {billData && (
                <Box
                  sx={{
                    bgcolor: 'rgba(69, 39, 160, 0.04)',
                    borderRadius: '14px',
                    p: 2.5,
                    mb: 3,
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem', mb: 0.5 }}>
                    Amount Paid
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    ₹{(billData.total || 0).toFixed(2)}
                  </Typography>
                </Box>
              )}

              {receiptData?.receipt_id && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  onClick={handleViewReceipt}
                  sx={{
                    borderRadius: '14px',
                    py: 1.2,
                    fontWeight: 600,
                    bordercolor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(69, 39, 160, 0.04)' },
                  }}
                >
                  View Receipt
                </Button>
              )}
            </Box>
          </Fade>
        </Box>
      </PageWrapper>
    );
  }

  // ── Bill Ready / Payment Initiated states ───────────────────────
  return (
    <PageWrapper>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4527A0 0%, #7E57C2 100%)',
          p: 3,
          pb: 2.5,
          color: 'rgba(255, 255, 255, 0.72)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <StorefrontRounded sx={{ fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {billData?.outlet_name || 'Restaurant'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75, fontSize: '0.72rem' }}>
              ORDER
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
              #{billData?.order_num}
            </Typography>
          </Box>
          {expiresIn > 0 && (
            <Chip
              label={`Expires in ${formatCountdown(expiresIn)}`}
              size="small"
              sx={{
                bgcolor: expiresIn < 120 ? 'rgba(255, 59, 48, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                color: 'rgba(255, 255, 255, 0.72)',
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 26,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Bill Items */}
      <Box sx={{ p: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 1.5 }}
        >
          ORDER DETAILS
        </Typography>

        <Box sx={{ mt: 1.5 }}>
          {(billData?.items || []).map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.2,
                borderBottom:
                  idx < (billData?.items?.length || 0) - 1
                    ? '1px solid #E0E0E3'
                    : 'none',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  {item.item_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                  ₹{(item.unit_price || 0).toFixed(2)} × {item.quantity}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                ₹{(item.amount || 0).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ₹{(billData?.subtotal || 0).toFixed(2)}
            </Typography>
          </Box>
          {(billData?.tax || 0) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Tax</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ₹{(billData?.tax || 0).toFixed(2)}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Total</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main' }}>
              ₹{(billData?.total || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Payment Section */}
      <Box sx={{ px: 3, pb: 3 }}>
        {status === STATUS.PAYMENT_INITIATED && paymentInfo ? (
          <Fade in timeout={400}>
            <Box>
              {/* UPI Payment Info */}
              <Box
                sx={{
                  bgcolor: '#F0FFF4',
                  border: '1px solid rgba(52, 199, 89, 0.2)',
                  borderRadius: '14px',
                  p: 2.5,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: '#34C759', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 1 }}
                >
                  PAY VIA UPI
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, mt: 0.5, fontFamily: 'monospace', letterSpacing: 0.5 }}
                >
                  {paymentInfo.upi_id || 'merchant@upi'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                  Amount: ₹{(paymentInfo.amount || billData?.total || 0).toFixed(2)}
                </Typography>
                {paymentInfo.reference && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                    Ref: {paymentInfo.reference}
                  </Typography>
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleConfirmPayment}
                disabled={status === STATUS.CONFIRMING}
                startIcon={
                  status === STATUS.CONFIRMING ? (
                    <CircularProgress size={18} sx={{ color: 'rgba(255, 255, 255, 0.72)' }} />
                  ) : (
                    <CheckCircleOutline />
                  )
                }
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  bgcolor: '#34C759',
                  '&:hover': { bgcolor: '#2DB14A' },
                  '&.Mui-disabled': { bgcolor: '#A8E6B0', color: 'rgba(255, 255, 255, 0.72)' },
                }}
              >
                {status === STATUS.CONFIRMING ? 'Confirming...' : 'I have paid'}
              </Button>
            </Box>
          </Fade>
        ) : (
          <Button
            fullWidth
            variant="contained"
            onClick={handleInitiatePayment}
            disabled={status === STATUS.PAYING}
            startIcon={
              status === STATUS.PAYING ? (
                <CircularProgress size={18} sx={{ color: 'rgba(255, 255, 255, 0.72)' }} />
              ) : (
                <CreditCard />
              )
            }
            sx={{
              borderRadius: '14px',
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: '#B39DDB', color: 'rgba(255, 255, 255, 0.72)' },
            }}
          >
            {status === STATUS.PAYING ? 'Processing...' : 'Pay Now'}
          </Button>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontSize: '0.68rem', textAlign: 'center', display: 'block' }}
        >
          Powered by Karobar
        </Typography>
      </Box>
    </PageWrapper>
  );
};
